/**
 * Title cell for the gated Hierarchy editor (PLT-5707): the product / business-unit
 * name and description, editable inline on double-click. Enter (name) or blur
 * commits; Esc cancels. Read-only consumers render the plain name + description,
 * matching the previous static cell.
 *
 * Editing lives here (per-row local state) rather than the separate edit dialog,
 * so renaming a business unit or tweaking a product name no longer needs a modal.
 */
import { TextInput, Textarea } from '@primer/react';
import { useState } from 'react';
import styled from 'styled-components';
import { PiuProductState } from '@upright/common/model/input/companies/products/product-input-ui/types';

interface Props {
  product: PiuProductState;
  editable: boolean;
  onCommit: (product: PiuProductState) => void;
}

type EditingField = 'title' | 'description' | null;

export function EditableProductTitleCell({ product, editable, onCommit }: Props) {
  const [editingField, setEditingField] = useState<EditingField>(null);
  const [draft, setDraft] = useState('');

  const startEditing = (field: Exclude<EditingField, null>) => {
    setDraft(field === 'title' ? product.title : product.description);
    setEditingField(field);
  };

  const commit = () => {
    const field = editingField;
    setEditingField(null);
    if (field === 'title') {
      const trimmed = draft.trim();
      // Ignore empty / unchanged titles — a name is required.
      if (trimmed === '' || trimmed === product.title) return;
      onCommit({ ...product, title: trimmed });
    } else if (field === 'description') {
      if (draft === product.description) return;
      onCommit({ ...product, description: draft });
    }
  };

  // Stop the row-level drag / expand handlers from swallowing the interaction
  // while editing (the row itself is draggable).
  const stop = (event: { stopPropagation: () => void }) => {
    event.stopPropagation();
  };

  return (
    <Cell>
      {editingField === 'title' ? (
        <TextInput
          autoFocus
          value={draft}
          aria-label="Name"
          draggable={false}
          onMouseDown={stop}
          onClick={stop}
          onChange={event => {
            setDraft(event.target.value);
          }}
          onBlur={commit}
          onKeyDown={event => {
            if (event.key === 'Enter') {
              event.preventDefault();
              commit();
            } else if (event.key === 'Escape') {
              event.preventDefault();
              setEditingField(null);
            }
          }}
          sx={{ width: '100%' }}
        />
      ) : (
        <Title
          $editable={editable}
          title={editable ? 'Double-click to edit' : undefined}
          onDoubleClick={
            editable
              ? event => {
                  stop(event);
                  startEditing('title');
                }
              : undefined
          }
        >
          {product.title}
        </Title>
      )}

      {editingField === 'description' ? (
        <Textarea
          autoFocus
          value={draft}
          aria-label="Description"
          draggable={false}
          rows={2}
          onMouseDown={stop}
          onClick={stop}
          onChange={event => {
            setDraft(event.target.value);
          }}
          onBlur={commit}
          onKeyDown={event => {
            // Enter inserts a newline in the description; blur / Esc end editing.
            if (event.key === 'Escape') {
              event.preventDefault();
              setEditingField(null);
            }
          }}
          sx={{ width: '100%' }}
        />
      ) : product.description ? (
        <Description
          $editable={editable}
          title={editable ? 'Double-click to edit' : undefined}
          onDoubleClick={
            editable
              ? event => {
                  stop(event);
                  startEditing('description');
                }
              : undefined
          }
        >
          {product.description}
        </Description>
      ) : editable ? (
        <DescriptionPlaceholder
          title="Double-click to edit"
          onDoubleClick={event => {
            stop(event);
            startEditing('description');
          }}
        >
          Add description
        </DescriptionPlaceholder>
      ) : null}
    </Cell>
  );
}

const Cell = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space__xs);
  // Breathing room between the expand chevron / drag handle and the name (PLT-5707).
  padding-left: var(--space__xs);
  width: 100%;
`;

const Title = styled.div<{ $editable: boolean }>`
  font-weight: 600;
  font-size: var(--font-size__md);
  color: var(--fgColor-default);
  cursor: ${props => (props.$editable ? 'text' : 'inherit')};
`;

const Description = styled.div<{ $editable: boolean }>`
  font-size: var(--text-body-size-small);
  color: var(--fgColor-muted);
  line-height: 1.6;
  cursor: ${props => (props.$editable ? 'text' : 'inherit')};
`;

const DescriptionPlaceholder = styled.div`
  font-size: var(--text-body-size-small);
  color: var(--fgColor-muted);
  font-style: italic;
  cursor: text;
`;
